import marked from 'marked';

// Pure, dependency-light renderer for the newsletter builder. Turns a blocks[] array +
// sending-customer branding into an email-safe HTML document AND a plain-text fallback.
// Used BOTH for the live preview and the actual send payload, so the preview is exactly
// what recipients receive. Must stay pure: never mutate inputs, no store/DOM access.
//
// We use `marked` (already bundled via preact-markdown) rather than `markdown-it`: the
// latter pulls in `entities`, whose broken ESM source maps spam the webpack build with
// source-map warnings. To keep markdown-it's `html: false` safety, we neutralise raw HTML
// tags by escaping `<`/`>` BEFORE parsing, so only marked-generated tags reach the output.

const MARKED_OPTIONS = { breaks: true, gfm: true };

// Neutralise raw HTML tags in markdown source (replicates markdown-it `html: false`).
function escapeMarkdownHtml(str) {
    return String(str || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export const UNSUB_TOKEN = '{{unsubscribeUrl}}';

const DEFAULT_ACCENT = '#1f8a8a';

export function escapeHtml(str) {
    if (str === undefined || str === null) {
        return '';
    }
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// `page` = the server-rendered public page (/article/...), readable without a login.
// Default is the hash route, which opens inside the app and needs a login.
export function articleUrl(hostname, slug, uuidv4, { page = false } = {}) {
    const host = hostname || 'dyrejournal.app';
    const s = slug || 'artikkel';
    return page ? `https://${host}/article/${s}/${uuidv4}` : `https://${host}/#/article/${s}/${uuidv4}`;
}

// The public page exists only when the domain customer has articlePages on and the block's
// article category is allowed by the optional category list (both come from branding).
// Blocks saved before articleCategory existed have no category and keep the hash link.
export function wantsArticlePage(branding, block) {
    const flag = branding && branding.articlePages;
    if (!(flag === 1 || flag === true)) return false;
    const categories = Array.isArray(branding.articlePageCategories) ? branding.articlePageCategories : [];
    if (categories.length === 0) return true;
    const category = typeof block?.articleCategory === 'string' ? block.articleCategory.toLowerCase() : '';
    return category !== '' && categories.includes(category);
}

function blockArticleUrl(block, branding) {
    return articleUrl(branding.hostname, block.articleSlug, block.articleUuidv4, { page: wantsArticlePage(branding, block) });
}

// ---- per-block HTML renderers -------------------------------------------------

function renderHero(block, accent) {
    const headline = escapeHtml(block.headline);
    const subtext = escapeHtml(block.subtext);
    const bg = block.bgColor || accent;
    const img = block.imageUrl
        ? `<tr><td style="padding:0;"><img src="${escapeHtml(block.imageUrl)}" width="600" style="display:block;width:100%;max-width:600px;height:auto;" alt="" /></td></tr>`
        : '';
    return `
    <tr><td style="background-color:${escapeHtml(bg)};padding:32px 28px;color:#ffffff;">
        <h1 style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:30px;line-height:1.2;color:#ffffff;font-weight:700;">${headline}</h1>
        ${subtext ? `<p style="margin:12px 0 0;font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.5;color:#ffffff;">${subtext}</p>` : ''}
    </td></tr>
    ${img}`;
}

function renderText(block) {
    const inner = marked(escapeMarkdownHtml(block.markdown || ''), MARKED_OPTIONS);
    return `<tr><td style="padding:18px 28px;font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.6;color:#222222;">${inner}</td></tr>`;
}

function renderArticle(block, branding) {
    const url = blockArticleUrl(block, branding);
    const title = escapeHtml(block.title);
    const ingress = escapeHtml(block.ingress);
    const cta = escapeHtml(block.ctaLabel || 'Les mer');
    const accent = branding.accentColor || DEFAULT_ACCENT;
    // Image placement relative to the text: 'top' (default, above text), 'left' or 'right'
    // (beside the text). Side layouts only apply when there actually is an image.
    const align = block.imageAlign === 'left' || block.imageAlign === 'right' ? block.imageAlign : 'top';
    const hasImg = !!block.imageUrl;

    const textHtml = `
                <h2 style="margin:0 0 8px;font-size:20px;line-height:1.3;color:#111111;">${title}</h2>
                ${ingress ? `<p style="margin:0 0 14px;font-size:15px;line-height:1.55;color:#444444;">${ingress}</p>` : ''}
                <a href="${escapeHtml(url)}" style="display:inline-block;background-color:${escapeHtml(accent)};color:#ffffff;text-decoration:none;font-size:14px;font-weight:700;padding:10px 18px;border-radius:999px;">${cta}</a>`;

    let inner;
    if (hasImg && align !== 'top') {
        // Image beside text. Two-column table; image in a fixed-width cell, text fills the rest.
        const imgCell = `<td width="190" valign="top" style="width:190px;padding:0;">
                    <img src="${escapeHtml(block.imageUrl)}" width="190" style="display:block;width:190px;max-width:190px;height:auto;border-radius:10px;" alt="" />
                </td>`;
        const gapCell = '<td width="16" style="width:16px;font-size:0;line-height:0;">&nbsp;</td>';
        const textCell = `<td valign="top" style="font-family:Arial,Helvetica,sans-serif;">${textHtml}</td>`;
        const cells = align === 'left' ? `${imgCell}${gapCell}${textCell}` : `${textCell}${gapCell}${imgCell}`;
        inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cells}</tr></table>`;
    } else {
        // Image on top (default), or text-only when there is no image.
        const img = hasImg
            ? `<img src="${escapeHtml(block.imageUrl)}" width="544" style="display:block;width:100%;max-width:544px;height:auto;border-radius:10px;margin-bottom:14px;" alt="" />`
            : '';
        inner = `${img}${textHtml}`;
    }

    return `
    <tr><td style="padding:14px 28px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #eaeaea;border-radius:10px;">
            <tr><td style="padding:18px;font-family:Arial,Helvetica,sans-serif;">
                ${inner}
            </td></tr>
        </table>
    </td></tr>`;
}

function renderImage(block) {
    const imgTag = `<img src="${escapeHtml(block.imageUrl)}" width="544" style="display:block;width:100%;max-width:544px;height:auto;border-radius:10px;" alt="" />`;
    const inner = block.linkUrl ? `<a href="${escapeHtml(block.linkUrl)}">${imgTag}</a>` : imgTag;
    return `<tr><td style="padding:14px 28px;">${inner}</td></tr>`;
}

function renderButton(block, accent) {
    const label = escapeHtml(block.label || 'Les mer');
    const url = escapeHtml(block.url || '#');
    return `
    <tr><td align="center" style="padding:18px 28px;">
        <a href="${url}" style="display:inline-block;background-color:${escapeHtml(accent)};color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:700;padding:13px 28px;border-radius:999px;">${label}</a>
    </td></tr>`;
}

function renderDivider(block) {
    if (block.style === 'space') {
        return '<tr><td style="height:24px;line-height:24px;font-size:0;">&nbsp;</td></tr>';
    }
    return '<tr><td style="padding:8px 28px;"><hr style="border:0;border-top:1px solid #e6e6e6;margin:0;" /></td></tr>';
}

function renderHighlight(block, accent) {
    const heading = escapeHtml(block.heading);
    const bg = block.bgColor || '#f1fbfb';
    const rows = Array.isArray(block.rows) ? block.rows : [];
    const rowsHtml = rows.map(r => `
        <tr><td style="padding:10px 0;font-family:Arial,Helvetica,sans-serif;">
            <strong style="display:block;font-size:15px;color:#111111;">${escapeHtml(r.title)}</strong>
            <span style="font-size:14px;color:#444444;">${escapeHtml(r.text)}</span>
        </td></tr>`).join('');
    return `
    <tr><td style="padding:14px 28px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${escapeHtml(bg)};border-radius:10px;">
            <tr><td style="padding:20px 22px;">
                ${heading ? `<h3 style="margin:0 0 8px;font-family:Arial,Helvetica,sans-serif;font-size:18px;color:${escapeHtml(accent)};">${heading}</h3>` : ''}
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${rowsHtml}</table>
            </td></tr>
        </table>
    </td></tr>`;
}

function renderFooter(branding) {
    const f = branding.footer || {};
    const logo = branding.logoUrl
        ? `<img src="${escapeHtml(branding.logoUrl)}" height="34" style="display:block;height:34px;width:auto;margin:0 auto 12px;" alt="${escapeHtml(f.name)}" />`
        : '';
    const lines = [f.name, f.address, [f.email, f.phone].filter(Boolean).join(' · '), f.orgNr ? `Org.nr: ${f.orgNr}` : '']
        .filter(Boolean)
        .map(l => escapeHtml(l))
        .join('<br>');
    return `
    <tr><td style="padding:26px 28px;background-color:#111111;text-align:center;font-family:Arial,Helvetica,sans-serif;color:#cccccc;font-size:12px;line-height:1.6;">
        ${logo}
        ${lines}
        <div style="margin-top:14px;">
            <a href="${UNSUB_TOKEN}" style="color:#9bd1d1;text-decoration:underline;font-size:12px;">Avslutt abonnement på e-post</a>
        </div>
    </td></tr>`;
}

const HTML_RENDERERS = {
    hero: (b, br) => renderHero(b, br.accentColor || DEFAULT_ACCENT),
    text: b => renderText(b),
    article: (b, br) => renderArticle(b, br),
    image: b => renderImage(b),
    button: (b, br) => renderButton(b, br.accentColor || DEFAULT_ACCENT),
    divider: b => renderDivider(b),
    highlight: (b, br) => renderHighlight(b, br.accentColor || DEFAULT_ACCENT),
};

// ---- per-block text renderers -------------------------------------------------

function blockToText(block, branding) {
    switch (block.type) {
        case 'hero':
            return [block.headline, block.subtext].filter(Boolean).join('\n');
        case 'text':
            return String(block.markdown || '');
        case 'article':
            return [block.title, block.ingress, blockArticleUrl(block, branding)]
                .filter(Boolean).join('\n');
        case 'image':
            return block.linkUrl || '';
        case 'button':
            return [block.label, block.url].filter(Boolean).join(': ');
        case 'highlight':
            return [block.heading]
                .concat((Array.isArray(block.rows) ? block.rows : []).map(r => `- ${[r.title, r.text].filter(Boolean).join(': ')}`))
                .filter(Boolean).join('\n');
        case 'divider':
            return '---';
        default:
            return '';
    }
}

// ---- public entrypoint --------------------------------------------------------

// The inbox preview line — the grey text an email client shows after the subject. Hidden in
// the rendered body (every client hides `display:none` here) but read by the client when it
// builds the preview. The second span is the standard run of zero-width characters that stops
// the preview bleeding into whatever the first real block happens to say.
function renderPreheader(previewText) {
    const text = String(previewText || '').trim();
    if (!text) {
        return '';
    }
    const pad = '&#847;&zwnj;&nbsp;'.repeat(60);
    const hide = 'display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;';
    return `<span style="${hide}">${escapeHtml(text)}</span><span style="${hide}">${pad}</span>`;
}

export function renderNewsletterHtml(blocks = [], branding = {}, options = {}) {
    const list = Array.isArray(blocks) ? blocks : [];
    const accent = branding.accentColor || DEFAULT_ACCENT;
    const { previewText = '' } = options;

    const bodyRows = list
        .map(block => {
            const renderer = HTML_RENDERERS[block.type];
            return renderer ? renderer(block, branding) : '';
        })
        .join('\n');

    const footerRow = renderFooter(branding);

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<style>
  body { margin:0; padding:0; background-color:#f2f3f5; -webkit-text-size-adjust:100%; }
  img { border:0; outline:none; text-decoration:none; -ms-interpolation-mode:bicubic; }
  table { border-collapse:collapse; }
  a { color:${accent}; }
  @media only screen and (max-width:600px) {
    .nl-container { width:100% !important; }
  }
</style>
</head>
<body>
${renderPreheader(previewText)}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f2f3f5;">
  <tr><td align="center" style="padding:18px 10px;">
    <table role="presentation" class="nl-container" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background-color:#ffffff;border-radius:10px;overflow:hidden;">
      ${bodyRows}
      ${footerRow}
    </table>
  </td></tr>
</table>
</body>
</html>`;

    // The preheader leads the plain-text part too. Nothing is hidden in plain text, so it
    // reads as the opening line rather than as a duplicate — which is exactly what it is:
    // the one-sentence summary of the mail.
    const textParts = list.map(b => blockToText(b, branding)).filter(Boolean);
    if (String(previewText || '').trim()) {
        textParts.unshift(String(previewText).trim());
    }
    const f = branding.footer || {};
    textParts.push('---');
    textParts.push([f.name, f.address, f.email, f.phone].filter(Boolean).join('\n'));
    textParts.push(`Avslutt abonnement: ${UNSUB_TOKEN}`);
    const text = textParts.join('\n\n');

    return { html, text };
}

export default renderNewsletterHtml;
